<template>
	<div>
		<PageHeader title="Cancellation reasons" />

		<div class="card">
			<div class="card-body">
				<div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
					<div>
						<h5 class="mb-0">Accounts with no reason recorded</h5>
						<div class="text-muted small">
							A cancelled account is never removed, so it never passes through the removal modal. This is
							where the reason gets filled in.
						</div>
					</div>
					<div class="btn-group">
						<button type="button" class="btn" :class="scope === 'paying' ? 'btn-primary' : 'btn-outline-secondary'" @click="setScope('paying')">
							Paying churn
						</button>
						<button type="button" class="btn" :class="scope === 'all' ? 'btn-primary' : 'btn-outline-secondary'" @click="setScope('all')">
							Everything
						</button>
					</div>
				</div>

				<div class="alert alert-danger py-2" v-if="error">{{ error }}</div>

				<div class="text-muted" v-if="loading">Loading...</div>

				<div class="alert alert-success py-2" v-else-if="!rows.length">
					Nothing waiting. Every cancelled account in this scope has a reason.
				</div>

				<table class="table align-middle" v-else>
					<thead>
						<tr>
							<th>Account</th>
							<th>Brand</th>
							<th>Status</th>
							<th>Cancelled</th>
							<th class="text-end">Reason</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="row in rows" :key="row.subscription_id">
							<td>
								<router-link :to="'/clients/edit/' + row.client_id">{{ row.name }}</router-link>
								<div class="text-muted small">{{ row.website }}</div>
							</td>
							<td>{{ brandLabel(row.brand) }}</td>
							<td><span class="badge bg-danger">{{ row.status }}</span></td>
							<td class="text-muted">{{ fmtDate(row.canceled_at || row.updated_at) }}</td>
							<td class="text-end">
								<button type="button" class="btn btn-sm btn-outline-warning" @click="open(row)">Add reason</button>
							</td>
						</tr>
					</tbody>
				</table>

				<div class="text-muted small" v-if="rows.length">
					{{ rows.length }} waiting{{ scope === 'paying' ? '. Trials that never started are hidden; switch to Everything to see them.' : '.' }}
				</div>
			</div>
		</div>

		<CancellationModal
			modal-id="worklistCancellationModal"
			mode="reason"
			:client-id="selected ? selected.client_id : null"
			:client-name="selected ? selected.name : ''"
			:subscriptions="selectedSubscriptions"
			@saved="onSaved"
		/>
	</div>
</template>

<script>
import PageHeader from '@/components/layout/PageHeader'
import CancellationModal from '@/components/cancellation/CancellationModal'
import restApi from '../../api/index.js'
import { handleErrors } from '../../actions/helpers'
import { Modal } from 'bootstrap'

export default {
	name: 'CancellationWorklist',
	components: { PageHeader, CancellationModal },
	data() {
		return { rows: [], scope: 'paying', loading: true, error: '', selected: null }
	},
	computed: {
		// the modal filters its reason list by the brands on the account, and offers the
		// subscriptions to tick; from here there is exactly one, the row that was clicked
		selectedSubscriptions() {
			if (!this.selected) return []
			return [{ id: this.selected.subscription_id, brand: this.selected.brand, status: this.selected.status }]
		},
	},
	mounted() {
		this.$modal = new Modal('#worklistCancellationModal')
		this.load()
	},
	methods: {
		load() {
			this.loading = true
			this.error = ''
			restApi
				.get('/cancellations/missing', { params: { scope: this.scope } })
				.then(res => {
					this.rows = res.data.data || []
				})
				.catch(e => {
					this.error = handleErrors(e, 'Could not load the list.')
				})
				.finally(() => {
					this.loading = false
				})
		},
		setScope(scope) {
			this.scope = scope
			this.load()
		},
		open(row) {
			this.selected = row
			this.$nextTick(() => this.$modal.show())
		},
		onSaved() {
			this.$modal.hide()
			this.rows = this.rows.filter(r => r.subscription_id !== (this.selected && this.selected.subscription_id))
			this.selected = null
			this.$swal.fire({ icon: 'success', title: 'Reason recorded', timer: 2000 })
		},
		brandLabel(key) {
			return { town_web: 'Town Web', heygov: 'HeyGov', clerkminutes: 'ClerkMinutes' }[key] || key
		},
		fmtDate(v) {
			if (!v) return '—'
			return new Date(v).toLocaleDateString()
		},
	},
}
</script>
